import {useEffect, useRef} from 'react';
import {useCookie} from '@sail/data';
import {useUpdateSessionMiradorIds} from './useUpdateSessionMiradorIds';
import {getStore} from '../store';

interface MiradorIds {
  muid: string | null;
  sid: string | null;
}

/**
 * Hook to get Mirador IDs from browser cookies.
 *
 * When Stripe.js loads, it sets Mirador cookies on the merchant domain for fraud detection:
 * - __stripe_mid: Merchant User ID (MUID) - Tracks user across visits to merchant's site (2 year TTL)
 * - __stripe_sid: Session ID (SID) - Tracks a single visit to merchant site (30 min TTL)
 *
 * This hook uses @sail/data's useCookie which automatically subscribes to cookie changes
 * and updates reactively when Stripe.js sets the cookies. This ensures the session is updated
 * as soon as the cookies become available, without manual polling.
 *
 * Note: GUID (Global User ID) is not available from cookies and would require
 * accessing Stripe.js's internal MetricsController API which is not reliably exposed.
 *
 * @returns MiradorIds object with muid and sid (null if cookies not yet set)
 */
export const useMiradorIds = (): MiradorIds => {
  const [muid] = useCookie('__stripe_mid');
  const [sid] = useCookie('__stripe_sid');
  const updateSession = useUpdateSessionMiradorIds();
  const hasUpdated = useRef(false);
  const store = getStore();
  const sessionState = store.useSelector((state) => state.state);

  useEffect(() => {
    if (
      (muid || sid) &&
      !hasUpdated.current &&
      (sessionState === 'checkout' || sessionState === 'requires_setup')
    ) {
      const ids = {muid, sid};
      updateSession(ids);
      hasUpdated.current = true;
    }
  }, [muid, sid, updateSession, sessionState]);

  return {muid, sid};
};
